import React, { useEffect, useState } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
} from '@mui/material'
import { Close as CloseIcon } from '@mui/icons-material'

// Small shared URL editor for the Catalog pages (TDC-1499 phase 3): brand
// rows edit their network brand-page URL, category rows their category URL.
// The caller owns the PATCH — `onSave` should throw (or reject) with a
// user-readable message to surface inline; resolving closes the dialog.

const isValidHttpUrl = (value: string): boolean => {
  if (!/^https?:\/\//i.test(value)) return false
  try {
    // eslint-disable-next-line no-new
    new URL(value)
    return true
  } catch {
    return false
  }
}

export function EditUrlDialog({
  open,
  title,
  label = 'URL',
  helperText,
  initialUrl,
  allowClear = true,
  saveLabel = 'Save',
  savingLabel = 'Saving…',
  showNameField = false,
  nameLabel = 'Name',
  initialName,
  onClose,
  onSave,
}: {
  open: boolean
  title: string
  label?: string
  helperText?: string
  initialUrl: string | null | undefined
  allowClear?: boolean
  // The URL-add flows relabel the action ("Start analysis", "Import").
  saveLabel?: string
  savingLabel?: string
  // Optional name field above the URL (category edit) — when shown, onSave
  // receives the trimmed name as its second argument.
  showNameField?: boolean
  nameLabel?: string
  initialName?: string | null
  onClose: () => void
  onSave: (url: string, name?: string) => Promise<void>
}) {
  const [value, setValue] = useState(initialUrl || '')
  const [nameValue, setNameValue] = useState(initialName || '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [nameError, setNameError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setValue(initialUrl || '')
      setNameValue(initialName || '')
      setError(null)
      setNameError(null)
    }
  }, [open, initialUrl, initialName])

  const handleSave = async () => {
    const trimmed = value.trim()
    const trimmedName = nameValue.trim()
    if (showNameField && !trimmedName) {
      setNameError('Enter a name.')
      return
    }
    if (!trimmed && !allowClear) {
      setError('Enter a URL.')
      return
    }
    if (trimmed && !isValidHttpUrl(trimmed)) {
      setError('Enter a full URL starting with http:// or https://')
      return
    }
    setSaving(true)
    setError(null)
    setNameError(null)
    try {
      await onSave(trimmed, showNameField ? trimmedName : undefined)
      onClose()
    } catch (e: any) {
      setError(e?.message || "Couldn't save. Please try again.")
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {title}
        <IconButton aria-label="Close" onClick={onClose} size="small" disabled={saving}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {showNameField && (
          <TextField
            autoFocus
            fullWidth
            size="small"
            label={nameLabel}
            value={nameValue}
            onChange={(e) => setNameValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSave()
            }}
            error={!!nameError}
            helperText={nameError}
            sx={{ mt: 1, mb: 2 }}
          />
        )}
        <TextField
          autoFocus={!showNameField}
          fullWidth
          size="small"
          label={label}
          placeholder="https://…"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSave()
          }}
          error={!!error}
          helperText={error || helperText}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" disableElevation onClick={handleSave} disabled={saving}>
          {saving ? savingLabel : saveLabel}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
